import {Component, ElementRef, EventEmitter, forwardRef, Input, NgZone, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Ace, edit, config} from 'ace-builds';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/keybinding-vim'

const ACE_BASE_PATH = 'https://cdn.jsdelivr.net/npm/ace-builds@1.2.9/src-noconflict/'

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'ace-editor',
  template: '',
  styleUrls: ['./ace-edit-wrapper.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AceEditorComponent),
    multi: true
  }]
})
export class AceEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Output() textChanged = new EventEmitter();
  @Output() textChange = new EventEmitter();
  @Input() style: any = {};
  private _options: any = {};
  private _readOnly = false;
  private _theme = 'monokai';
  private _mode = 'html';
  private _autoUpdateContent = true;
  private _editor?: Ace.Editor;
  private _durationBeforeCallback = 0;
  private _text = '';
  oldText: any;
  timeoutSaving: any;
  private _basePath?: string;

  constructor(private elementRef: ElementRef, private zone: NgZone) {
  }

  ngOnInit() {
    const el = this.elementRef.nativeElement;
    this.setBasePath(this._basePath ?? ACE_BASE_PATH)
    this._editor = edit(el, {
      mode: this.formatMode(this._mode),
      theme: this.formatTheme(this._theme),
      readOnly: this._readOnly,
    });
    this._editor.setOptions(this._options ?? {})
    this.initEvents();
  }

  ngOnDestroy() {
    this._editor.destroy();
  }

  initEvents() {
    this._editor.on('change', () => this.updateText());
    this._editor.on('paste', () => this.updateText());
  }

  updateText() {
    const newVal = this._editor.getValue();
    if (newVal === this.oldText) {
      return;
    }
    if (!this._durationBeforeCallback) {
      this._text = newVal;
      this.zone.run(() => {
        this.textChange.emit(newVal);
        this.textChanged.emit(newVal);
      });
      this._onChange(newVal);
    } else {
      if (this.timeoutSaving) {
        clearTimeout(this.timeoutSaving);
      }

      this.timeoutSaving = setTimeout(() => {
        this._text = newVal;
        this.zone.run(() => {
          this.textChange.emit(newVal);
          this.textChanged.emit(newVal);
        });
        this.timeoutSaving = null;
      }, this._durationBeforeCallback);
    }
    this.oldText = newVal;
  }

  @Input() set options(options: any) {
    this.setOptions(options);
  }

  setOptions(options: { [p: string]: any }) {
    this._options = options;
    this._editor?.setOptions(options || {});
  }

  @Input() set readOnly(readOnly: boolean) {
    this.setReadOnly(readOnly);
  }

  setReadOnly(readOnly: boolean) {
    this._readOnly = readOnly;
    this._editor?.setReadOnly(readOnly);
  }

  @Input() set theme(theme: string) {
    this.setTheme(theme);
  }

  setTheme(theme: string) {
    this._theme = theme;
    this._editor?.setTheme(this.formatTheme(theme));
  }

  @Input() set mode(mode) {
    this.setMode(mode);
  }

  setMode(mode) {
    this._mode = mode;
    const formatted = this.formatMode(this._mode);
    this._editor?.getSession().setMode(formatted);
  }

  get value() {
    return this.text;
  }

  @Input()
  set value(value: string) {
    this.setText(value);
  }

  writeValue(value: any) {
    this.setText(value);
  }

  private _onChange = (_: any) => {
  };

  registerOnChange(fn: any) {
    this._onChange = fn;
  }

  private _onTouched = () => {
  };

  registerOnTouched(fn: any) {
    this._onTouched = fn;
  }

  get text() {
    return this._text;
  }

  @Input()
  set text(text: string) {
    this.setText(text);
  }

  setText(text: any) {
    if (text === null || text === undefined) {
      text = '';
    }
    if (this._text !== text && this._autoUpdateContent === true) {
      this._text = text;
      this._editor?.setValue(text);
      this._onChange(text);
      this._editor?.clearSelection();
    }
  }

  @Input() set autoUpdateContent(status: any) {
    this.setAutoUpdateContent(status);
  }

  setAutoUpdateContent(status: any) {
    this._autoUpdateContent = status;
  }

  @Input() set durationBeforeCallback(num: number) {
    this.setDurationBeforeCallback(num);
  }

  setDurationBeforeCallback(num: number) {
    this._durationBeforeCallback = num;
  }


  @Input() set basePath(path: string) {
    this.setBasePath(path)
  }

  setBasePath(path: string) {
    this._basePath = path;

    if (!this._editor) {
      config.set('basePath', path)
    }
  }

  formatTheme(theme: string) {
    return `ace/theme/${theme}`;
  }

  formatMode(mode) {
    return typeof mode === 'object' ? mode : `ace/mode/${mode}`;
  }

  getEditor(): Ace.Editor | undefined {
    return this._editor
  }

  switchToViMode(onWrite: () => void) {
    console.log('Switching to vi MODE');
    this._editor.setKeyboardHandler('ace/keyboard/vim');

    config.loadModule('ace/keyboard/vim', (module) => {
      const VimApi = module.CodeMirror.Vim;
      VimApi.defineEx('write', 'w', onWrite);
    });
  }
}
